import { computeSolanaTokenHash, solanaPoolAddresses } from "@stealthos/sdk";
import { selectForAmount } from "../spendSelection";
import { flatFieldHex } from "../notes";
import {
  currentTreeContext,
  spendableInTree,
  selfDelivery,
  type SolanaOperationBase,
  type SolanaOperationResult,
} from "./operations";
import { createSolanaFlatNote } from "./notes";
import { proveAndRelaySolanaSettlement } from "./settle";
import { fail } from "./common";
import { validateSolanaSwapQuote, type SolanaSwapApproval } from "./swapQuote";
import { resolveStealthHandle } from "../../utils/handle-resolver";
import type { SolanaJournalDelivery } from "./journal";

async function verifyFeeRecipients(
  approved: SolanaSwapApproval["quote"],
  signal?: AbortSignal,
) {
  if (approved.feePolicy !== "private-input-fees") return;
  for (const receiver of [
    approved.feeRecipients!.protocol,
    approved.feeRecipients!.partner,
  ]) {
    if (!receiver) continue;
    const resolved = await resolveStealthHandle(receiver.handle, "base", {
      network: "solana-devnet",
      signal,
    });
    if (
      resolved.mpkHex?.toLowerCase() !== receiver.mpk.toLowerCase() ||
      resolved.inboxPublicKeyHex?.toLowerCase() !==
        receiver.inboxPublicKey.toLowerCase() ||
      resolved.version !== receiver.version
    )
      fail("Swap fee handle changed. Get a fresh quote.");
  }
}

export async function quoteSolanaSwap(
  params: SolanaOperationBase & {
    outputMint: string | null;
    amount: bigint;
    slippageBps: number;
  },
): Promise<SolanaSwapApproval> {
  const { ctx } = params;
  if (!ctx.relay.quoteSwap) fail("Orca quoting is unavailable");
  if (params.amount <= 0n) fail("enter a positive amount");
  const { currentTreeIndex } = await currentTreeContext(ctx, params.mint);
  const available = await spendableInTree(params, currentTreeIndex);
  const selected = selectForAmount(available, params.amount);
  if (selected.length > 13)
    fail("swap balance is too fragmented; consolidate notes first");
  const inputTotal = selected.reduce((sum, n) => sum + n.note.value, 0n);
  const request = {
    network: ctx.network.key,
    programId: ctx.network.programId,
    kind: "swap" as const,
    mint: params.mint,
    outputMint: params.outputMint,
    treeIndex: currentTreeIndex,
    inputCount: selected.length,
    outputCount: inputTotal > params.amount ? 1 : 0,
    amount: params.amount.toString(),
    slippageBps: params.slippageBps,
  };
  const quote = validateSolanaSwapQuote(
    await ctx.relay.quoteSwap(request, { signal: ctx.signal }),
    request,
  );
  await verifyFeeRecipients(quote, ctx.signal);
  return {
    quote,
    selectedCommitments: selected.map((n) => n.note.commitment),
    inputTotal,
  };
}

export async function executeSolanaSwap(
  params: SolanaOperationBase & { approved: SolanaSwapApproval },
): Promise<SolanaOperationResult> {
  const { ctx, approved } = params;
  const { currentTreeIndex, currentTokenHash, feeRecipient } =
    await currentTreeContext(ctx, params.mint);
  const available = await spendableInTree(params, currentTreeIndex);
  const selected = approved.selectedCommitments.map(
    (c) =>
      available.find(
        (n) => n.note.commitment.toLowerCase() === c.toLowerCase(),
      ) ?? fail("quoted notes are no longer spendable"),
  );
  if (
    selected.length < 1 ||
    selected.length > 13 ||
    new Set(approved.selectedCommitments.map((c) => c.toLowerCase())).size !==
      selected.length
  )
    fail("invalid swap input selection");
  const total = selected.reduce((sum, n) => sum + n.note.value, 0n);
  const amount = BigInt(approved.quote.request.amount);
  const q = validateSolanaSwapQuote(approved.quote, {
    ...approved.quote.request,
    network: ctx.network.key,
    programId: ctx.network.programId,
    mint: params.mint,
    treeIndex: currentTreeIndex,
    inputCount: selected.length,
    outputCount: total > amount ? 1 : 0,
  });
  await verifyFeeRecipients(q, ctx.signal);
  if (
    total < amount ||
    total !== approved.inputTotal ||
    feeRecipient !== q.feeRecipient
  )
    fail("quote no longer matches this private balance or fee recipient");
  const change =
    total > amount
      ? [
          await createSolanaFlatNote({
            recipientMpk: ctx.spendingKeys.mpk,
            network: ctx.network,
            treeIndex: currentTreeIndex,
            mint: params.mint,
            tokenHash: currentTokenHash,
            value: total - amount,
          }),
        ]
      : [];
  const privateFees = q.feePolicy === "private-input-fees";
  const outputs = [];
  const deliveries: SolanaJournalDelivery[] = [];
  const feeRandom = { protocol: 0n, partner: 0n };
  for (const kind of ["protocol", "partner"] as const) {
    const amount = BigInt(kind === "protocol" ? q.protocolFee : q.partnerFee);
    if (!privateFees || amount === 0n) continue;
    const recipient = q.feeRecipients![kind]!;
    const note = await createSolanaFlatNote({
      recipientMpk: BigInt(recipient.mpk),
      network: ctx.network,
      treeIndex: currentTreeIndex,
      mint: params.mint,
      tokenHash: currentTokenHash,
      value: amount,
    });
    outputs.push(note);
    feeRandom[kind] = note.random;
    deliveries.push({
      commitment: note.commitment,
      recipientMpkHex: recipient.mpk,
      recipientInboxPublicKeyHex: recipient.inboxPublicKey,
      keepLocal: BigInt(recipient.mpk) === ctx.spendingKeys.mpk,
    });
  }
  outputs.push(...change);
  deliveries.push(...change.map((n) => selfDelivery(n, ctx.spendingKeys.mpk)));
  if (selected.length + outputs.length > 14)
    fail("swap balance is too fragmented; consolidate notes first");
  const seedNote = await createSolanaFlatNote({
    recipientMpk: ctx.spendingKeys.mpk,
    network: ctx.network,
    treeIndex: currentTreeIndex,
    mint: q.request.outputMint,
    tokenHash: computeSolanaTokenHash({
      cluster: ctx.network.cluster,
      programId: ctx.network.programId,
      poolConfig: solanaPoolAddresses(ctx.network.programId).poolConfig,
      treeIndex: currentTreeIndex,
      mint: q.request.outputMint,
    }),
    value: 1n,
  });
  const settled = await proveAndRelaySolanaSettlement(ctx, {
    kind: "swap",
    mint: params.mint,
    selected,
    outputs,
    withdrawAmount:
      amount -
      (privateFees ? BigInt(q.protocolFee) + BigInt(q.partnerFee) : 0n),
    fee: BigInt(q.fee),
    swap: {
      outputMint: q.request.outputMint,
      whirlpool: q.whirlpool,
      minOutputAmount: BigInt(q.minOutputAmount),
      outputNpk: flatFieldHex(seedNote.npk) as `0x${string}`,
      deadlineSlot: BigInt(q.deadlineSlot),
      ...(privateFees
        ? {
            partnerId: q.feePolicyHash!,
            privateFees: {
              grossAmount: amount,
              protocolFee: BigInt(q.protocolFee),
              partnerFee: BigInt(q.partnerFee),
              protocolMpk: feeRandom.protocol
                ? q.feeRecipients!.protocol!.mpk
                : (flatFieldHex(0n) as `0x${string}`),
              protocolRandom: flatFieldHex(feeRandom.protocol) as `0x${string}`,
              partnerMpk: feeRandom.partner
                ? q.feeRecipients!.partner!.mpk
                : (flatFieldHex(0n) as `0x${string}`),
              partnerRandom: flatFieldHex(feeRandom.partner) as `0x${string}`,
            },
          }
        : {}),
    },
    swapSeed: {
      random: seedNote.random,
      recipientMpk: ctx.spendingKeys.mpk,
      outputMint: q.request.outputMint,
    },
    deliveries,
  });
  return {
    success: true,
    signature: settled.signature,
    slot: settled.slot,
    spentNotes: selected.map((n) => n.note),
    outputNotes: settled.outputs,
    journalId: settled.journalId,
    replayed: settled.replayed,
  };
}
